import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import {
  EmployeeScoreFormRule,
  EmployeeScoreFormSchema,
} from "@core/form/form.rule";
import { useFormDefaultEmployeeScore } from "@core/form/form-default-employee-score.hook";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  useQyUpdateEmployeeScore,
  useQyGetEmployeeScoreById,
  useQyDeleteEmployeeScore,
} from "@core/query/employee-score.query";
import { GetResponseEmployeeScoreModel } from "@core/model/query-model-employee-score";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditEmployeeScore() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultEmployeeScore();
  const navigate = useNavigate();
  const { mapUpdateEmployeeScore, mapDeleteEmployeeScore } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { employeeScoreId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID EmployeeScore
  const handleGetApiSuccess = (data: GetResponseEmployeeScoreModel) => {
    if (data && data.count && data.count > 0) {
      const responseData = data.data?.[0];
      setValue("userCode", responseData?.user_code || "");
      setValue("bucketCode", responseData?.bucket_code || "");
      setValue("branchCode", responseData?.branch_code || "");
      // setValue("questionCode", responseData?.question_code || "");
      // setValue("questionLabel", responseData?.question_name || "");
      // setValue("score", responseData?.score);
      // setValue(
      //   "effectivityDate",
      //   formatDate(responseData?.effectivity_date) as any,
      //   undefined
      // );
    }
  };
  const {
    data: employeeScoreResponse,
    isLoading,
    isError: employeeScoreError,
  } = useQyGetEmployeeScoreById(employeeScoreId || "", handleGetApiSuccess);
  const employeeScoreData = employeeScoreResponse?.data?.[0];

  // API UPDATE EmployeeScore
  const handleUpdateApiSuccess = () => {
    showSuccess("EmployeeScore updated successfully");
  };
  const { mutate: updateEmployeeScore, isLoading: isEditLoading } =
    useQyUpdateEmployeeScore(handleUpdateApiSuccess);

  // API DELETE EmployeeScore
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("EmployeeScore deleted successfully");
  };
  const { mutate: deleteEmployeeScore } = useQyDeleteEmployeeScore(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<EmployeeScoreFormSchema>({
    defaultValues: getDefault(employeeScoreData),
    resolver: zodResolver(EmployeeScoreFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: EmployeeScoreFormSchema) => {
    if (!employeeScoreData) {
      throw new Error("No employeeScore data");
    }

    // const formData = mapUpdateEmployeeScore(form, employeeScoreData.code);
    // updateEmployeeScore(formData);
  };
  const handleValidateError = (err: FieldErrors<EmployeeScoreFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!employeeScoreData) {
      throw new Error("No employeeScore data found");
    }

    // const form = mapDeleteEmployeeScore(employeeScoreData.code);
    // deleteEmployeeScore(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    employeeScoreId,
    employeeScoreData,
    isLoading,
    employeeScoreError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
